/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import {
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  ListSubheader,
  makeStyles,
  Typography
} from '@material-ui/core';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import { MyLocation } from '@material-ui/icons';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import HelpIcon from '@material-ui/icons/Help';
import PropTypes from 'prop-types';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import {
  AlertCircle as AlertIcon, AlignJustify as AlignJustifyIcon, Archive as ArchiveIcon,
  Box as BoxIcon, Calendar as CalendarIcon, List as ListIcon, PieChart as PieChartIcon, 
  Settings as SettingsIcon, ShoppingBag as ShoppingBagIcon, Square as SquareIcon, 
  User as UserIcon, Users as UsersIcon,CreditCard as TunnelIcon, ArrowDownCircle as Discount,
  BarChart as BarChartIcon,
  AlertTriangle as AlertTriangleIcon,
  Key as KeyIcon,
  DollarSign as DollarSignIcon
} from 'react-feather';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Link as RouterLink, matchPath, useLocation } from 'react-router-dom';
import Logo from 'src/components/Logo';
import Truncate from 'src/components/Truncate';
import usePolicy from 'src/hooks/usePolicy';
import { Policies } from 'src/types/policies';
import NavItem from './NavItem';
import useCheckRole from 'src/hooks/useCheckRole';
import { UserRoles } from 'src/types/user';
import useAuth from 'src/hooks/useAuth';
import LocalOfferIcon from '@material-ui/icons/LocalOffer'; 

interface NavBarProps {
  onMobileClose: () => void;
  openMobile: boolean;
  clientName?: string;
}

interface Item {
  href?: string;
  icon?: ReactNode;
  info?: ReactNode;
  items?: Item[];
  title: string;
}

function renderNavItems({
  items,
  pathname,
  depth = 0
}: {
  items: Item[];
  pathname: string;
  depth?: number;
}) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth
}: {
  acc: any[];
  pathname: string;
  item: Item;
  depth: number;
}) {
  const key = item.title + depth;
  if (item.items) {
    const open = matchPath({
      path: item.href,
      caseSensitive: false,
      end: false
    }, pathname);
    
    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

const NavBar: FC<NavBarProps> = ({ onMobileClose, openMobile, clientName }) => {
  const classes = useStyles();
  const location = useLocation();
  const canReadClients = usePolicy(Policies.SuperAdmin);
  const canReadApiKeys = usePolicy(Policies.Administrator);
  const canReadClientIntegrations = usePolicy(Policies.SuperAdmin);
  const canReadGlobalProfiles = usePolicy(Policies.SuperAdmin);
  const canReadUsers = usePolicy(Policies.Administrator);
  const canReadProducts = usePolicy(Policies.StoreManager);
  const canReadStores = usePolicy(Policies.StoreManager);
  const canReadStationGroups = usePolicy(Policies.StoreManager);
  const canReadStations = usePolicy(Policies.FloorManager);
  const canReadDiscounts = usePolicy(Policies.StoreManager);
  const canReadLocations = usePolicy(Policies.Administrator);
  const canReadUnknownItems = usePolicy(Policies.StoreManager);
  const canReadDashboard = usePolicy(Policies.FloorManager);
  const canReadTransactionHistory = usePolicy(Policies.FloorManager);
  const canReadTunnelHistory = usePolicy(Policies.TunnelManager);
  const canReadAlertsAndNotifications = usePolicy(Policies.StoreManager);
  const canReadOperatingSchedule = usePolicy(Policies.Administrator);
  const canReadTunnels = usePolicy(Policies.Administrator);
  const canReadLossMitigation = usePolicy(Policies.LossDetection);
  const canReadProductImports = usePolicy(Policies.Administrator);
  const [managementMenus, setManagementMenus] = useState([]);
  const [dashboardMenus, setDashboardMenus] = useState([]);
  const [reportsMenus, setReportsMenus] = useState([]);
  const sections = [];
  const mgmMenus = [];
  const dBoardMenus = [];

  const { user } = useAuth();
  const istunnelManager = useCheckRole(UserRoles.tunnelManager, user);
  const isSuperAdmin = usePolicy(Policies.SuperAdmin);
  if (dashboardMenus.length) {
    sections.push({
      subheader: 'Dashboards',
      items: dashboardMenus,
    });
  }
  if (reportsMenus.length) {
    sections.push({
      subheader: 'Reports',
      items: reportsMenus,
    });
  }
  if (managementMenus.length) {
    sections.push({
      subheader: 'Management',
      items: managementMenus,
    });
  }

  useEffect(() => {
    if (canReadDashboard) {
      dBoardMenus.push({
        title: 'Main Dashboard',
        icon: PieChartIcon,
        href: '/app/reports/dashboard'
      });
    }
    if(!istunnelManager)
    {
      dBoardMenus.push({
        title: 'Clerk Dashboard',
        icon: DesktopWindowsIcon,
        href: '/app/reports/clerk-dashboard'
      });
    }
    if (canReadLossMitigation) {
      dBoardMenus.push({
        title: 'Loss Detection',
        icon: AlertTriangleIcon,
        href: '/app/reports/loss-detection',
      });
    }

    if (canReadUnknownItems) {
      reportsMenus.push({
        title: 'Unknown Items',
        icon: HelpIcon,
        href: '/app/reports/unknown-items'
      });
    }
    if (canReadTransactionHistory) {
      reportsMenus.push({
        title: 'Transaction History',
        icon: AlignJustifyIcon,
        href: '/app/reports/transaction'
      });
      reportsMenus.push({
        title: 'Cash Transactions',
        icon: DollarSignIcon,
        href: '/app/reports/cash-transaction'
      });
    }
    if (canReadTunnelHistory) {
      reportsMenus.push({
        title: 'Tunnel History',
        icon: AlignJustifyIcon,
        href: '/app/reports/tunnel'
      });
    }
    if (canReadProductImports) {
      reportsMenus.push({
        title: 'Import History',
        icon: ListIcon,
        href: '/app/management/import-product-history',
      });
    }
    if (canReadLossMitigation) {
      reportsMenus.push({
        title: 'Loss Analytics',
        icon: BarChartIcon,
        href: '/app/reports/loss-analytics',
      });
    }
    if (canReadClients || canReadApiKeys){
      const clientSubItems = [];
      if (canReadClients) {
        clientSubItems.push({
          title: 'Manage Clients',
          href: '/app/management/clients/directory',
        });
      }
      if (canReadApiKeys){
        clientSubItems.push({
          title: 'API Keys',
          href: '/app/management/clients/api-keys',
        });
      }
      mgmMenus.push({
        title: 'Clients',
        icon: UserIcon,
        items: clientSubItems,
        href: '/app/management/clients',
      });
    }
    if (isSuperAdmin){
      mgmMenus.push({
        title: 'Global API Keys',
        icon: KeyIcon,
        href: '/app/management/global-api-keys',
      });
    }
     if (canReadClientIntegrations)
    {
      mgmMenus.push({
        title: 'Integrations',
        icon: DeviceHubIcon,
        href: '/app/management/client-integration',
      });
     }
    if (canReadUsers) {
      mgmMenus.push({
        title: 'Users',
        icon: UsersIcon,
        href: '/app/management/users',
      });
    }
    if (canReadProducts) {
      mgmMenus.push({
        title: 'Products',
        icon: BoxIcon,
        href: '/app/management/products',
      });
    }
    if (canReadStores) {
      mgmMenus.push({
        title: 'Stores',
        icon: ShoppingBagIcon,
        href: '/app/management/stores',
      });
    }
    if (canReadStationGroups) {
      mgmMenus.push({
        title: 'Station Groups',
        icon: SquareIcon,
        href: '/app/management/station-groups',
      });
    }
    if (canReadStations) {
      mgmMenus.push({
        title: 'Stations',
        icon: ArchiveIcon,
        href: '/app/management/stations',
      });
    }
    if (canReadDiscounts) {
      mgmMenus.push({
        title: 'Discounts',
        icon: LocalOfferIcon,
        href: '/app/management/discounts',
      });
    }
    if (canReadLocations) {
      mgmMenus.push({
        title: 'Locations',
        icon: MyLocation,
        href: '/app/management/locations',
      });
    }
    if (canReadTunnels) {
      mgmMenus.push({
        title: 'Tunnels',
        icon: TunnelIcon,
        href: '/app/management/tunnels',
      });
    }
    if (canReadGlobalProfiles) {
      mgmMenus.push({
        title: 'Global Profiles',
        icon: SettingsIcon,
        href: '/app/management/global-profiles',
      });
    }
    if (canReadAlertsAndNotifications) {
      mgmMenus.push({
        title: 'Alerts & Notifications',
        icon: AlertIcon,
        href: '/app/reports/alerts-and-notifications'
      });
    }
    if (canReadOperatingSchedule) {
      mgmMenus.push({
        title: 'Operating Schedule',
        icon: CalendarIcon,
        href: '/app/reports/operating-schedule'
      });
    }
    setManagementMenus([...mgmMenus, ...managementMenus]);
    setDashboardMenus([...dBoardMenus, ...dashboardMenus]);
    setReportsMenus([...reportsMenus]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canReadClients,
    canReadClientIntegrations,
    canReadGlobalProfiles,
    canReadUsers,
    canReadProducts,
    canReadStores,
    canReadStationGroups,
    canReadStations,
    canReadDiscounts,
    canReadLocations,
    canReadDashboard,
    canReadAlertsAndNotifications,
    canReadTransactionHistory,
    canReadTunnelHistory,
    canReadOperatingSchedule,
    canReadTunnels,
    canReadApiKeys,
    canReadLossMitigation,
    canReadProductImports
  ]);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box
            p={2}
            display="flex"
            justifyContent="center"
          >
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
          <Box
            p={1}
            display="flex"
            justifyContent="center"
          >
            <Typography variant="h5">
              <Truncate value={clientName ?? ''} />
            </Typography>
          </Box>

        </Hidden>

        <Divider />
        <Box p={2}>
          {sections.map((section) => (
            <List
              key={section.subheader}
              subheader={(
                <ListSubheader
                  disableGutters
                  disableSticky
                >
                  {section.subheader}
                </ListSubheader>
              )}
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
